import type { StudentTestAnswer, StudentTestContent } from "p6m-p6u";
import { ShareModes } from "../enums/shareFlow";
import { toEpochMs } from "../utils/dateUtils";
import { ActivityVisibility, resolveTestActivityVisibility } from "./ActivityVisibility";
import { resolveShareModeWithLegacy } from "./LegacyTest";

const THREE_WEEKS_MS = 21 * 24 * 60 * 60 * 1000;

const _parseJsonBlob = <T>(json: string, warnLabel: string): T | null => {
    try {
        return JSON.parse(json) as T;
    } catch (e) {
        console.warn(`Failed to parse ${warnLabel}`, e);
        return null;
    }
};

export interface ClassMemberActivity {
    testId: string;
    name: string;
    shareMode: ShareModes;
    timeLimit?: string;
    dueDate?: number;
    visibilityDate: number | null;
}

// Entry of the aea/student-tests-class/ response; `json` is a stringified test blob.
export interface ClassMemberClassTestRaw {
    // Epoch ms; legacy rows may hold ISO strings.
    dueDate?: number | string | null;
    visibilityDate?: number | string | null;
    deletedDate?: number;
    json?: string;
}

// Entry of the aea/vocabulary-training-metadata/class response array.
export interface ClassMemberVocabTrainingRaw {
    testUuid?: string;
    testName?: string;
    vocabularyCount?: number;
    shareLink?: string;
    // Epoch ms; legacy rows may hold ISO strings.
    visibilityDate?: number | string | null;
    updatedDate?: number | string;
    deletedDate?: number;
}

export interface ClassMemberVocabActivity {
    testUuid: string;
    name: string;
    wordCount: number | null;
    shareLink: string;
    visibilityDate: number | null;
}

export enum ClassMemberActivityStatuses {
    NEW = "NEW",
    SUBMITTED = "SUBMITTED",
    EVALUATED = "EVALUATED",
}

export interface ClassMemberActivityItem {
    activity: ClassMemberActivity;
    status: ClassMemberActivityStatuses;
    result: StudentTestAnswer | null;
}

export enum ClassMemberDashboardItemKinds {
    TEST = "TEST",
    VOCAB = "VOCAB",
}

export type ClassMemberDashboardItem =
    | ({ kind: ClassMemberDashboardItemKinds.TEST } & ClassMemberActivityItem)
    | { kind: ClassMemberDashboardItemKinds.VOCAB; activity: ClassMemberVocabActivity };

export interface BucketedClassMemberActivities {
    current: ClassMemberDashboardItem[];
    evaluations: ClassMemberDashboardItem[];
    older: ClassMemberDashboardItem[];
}

export const extractClassMemberActivities = (rows: Record<string, ClassMemberClassTestRaw>): ClassMemberActivity[] => {
    const activities: ClassMemberActivity[] = [];

    Object.entries(rows).forEach(([testId, raw]) => {
        if (raw.deletedDate || !raw.json) return;

        const parsed = _parseJsonBlob<StudentTestContent>(raw.json, `class member activity ${testId}`);
        if (!parsed) return;

        activities.push({
            testId,
            name: parsed.name ?? "",
            shareMode: resolveShareModeWithLegacy(parsed),
            timeLimit: parsed.timeLimit || undefined,
            dueDate: toEpochMs(raw.dueDate) ?? undefined,
            visibilityDate: toEpochMs(raw.visibilityDate),
        });
    });

    return activities;
};

export const extractClassMemberVocabActivities = (rows: ClassMemberVocabTrainingRaw[]): ClassMemberVocabActivity[] =>
    rows.flatMap((raw) => {
        if (raw.deletedDate || !raw.testUuid) return [];
        return [
            {
                testUuid: raw.testUuid,
                name: raw.testName ?? "",
                wordCount: raw.vocabularyCount ?? null,
                shareLink: raw.shareLink ?? "",
                visibilityDate: toEpochMs(raw.visibilityDate),
            },
        ];
    });

/**
 * A stored result row IS the submission — there is no separate submitted flag. Practices are exempt:
 * they are explicitly repeatable, so their result rows must never read as "already done".
 *
 * Note the asymmetry: a result proves a submission happened, but its absence only proves none was
 * found — a failed lookup looks identical, so callers that gate on this must decide for themselves
 * how to treat a fetch error.
 */
export const hasClassMemberSubmitted = (
    activity: Pick<ClassMemberActivity, "shareMode">,
    result: StudentTestAnswer | null
): result is StudentTestAnswer => {
    if (activity.shareMode === ShareModes.PRACTICE) return false;
    return result !== null;
};

/**
 * sharedInClassroomDate is deliberately the ONLY evaluation signal: a result link (sharingId) is
 * auto-minted for every fully graded result as soon as the teacher opens the share page — treating it
 * as "shared" would surface evaluations the teacher never released.
 */
export const resolveClassMemberActivityStatus = (
    activity: ClassMemberActivity,
    result: StudentTestAnswer | null
): ClassMemberActivityStatuses => {
    if (!hasClassMemberSubmitted(activity, result)) return ClassMemberActivityStatuses.NEW;
    if (result.sharedInClassroomDate) return ClassMemberActivityStatuses.EVALUATED;
    return ClassMemberActivityStatuses.SUBMITTED;
};

export interface ClassMemberTestResultRow extends StudentTestAnswer {
    json?: string;
}

export const parseClassMemberResultRow = (id: string, row: ClassMemberTestResultRow): StudentTestAnswer | null => {
    if (!row.json) return { ...row, id };

    const parsed = _parseJsonBlob<StudentTestAnswer>(row.json, `class member result ${id}`);
    return parsed ? { ...parsed, id } : null;
};

export const extractAllResults = (rows: Record<string, ClassMemberTestResultRow>): StudentTestAnswer[] =>
    Object.entries(rows)
        .map(([id, row]) => parseClassMemberResultRow(id, row))
        .filter((result): result is StudentTestAnswer => result !== null);

export const extractResultsForMember = (
    rows: Record<string, ClassMemberTestResultRow>,
    classMemberId: string
): StudentTestAnswer[] => extractAllResults(rows).filter((result) => result.classMemberId === classMemberId);

/**
 * The classroom-shared evaluation wins over newer submissions (it's what the class member views);
 * otherwise the latest submission represents the member's state.
 */
export const pickRelevantClassMemberResult = (results: StudentTestAnswer[]): StudentTestAnswer | null => {
    if (results.length === 0) return null;

    const sharedInClassroom = results.find((result) => result.sharedInClassroomDate);
    if (sharedInClassroom) return sharedInClassroom;

    return results.reduce((latest, result) => ((result.sentTime ?? 0) > (latest.sentTime ?? 0) ? result : latest));
};

const isWithinLastThreeWeeks = (date: number | null | undefined): boolean => {
    if (date == null) return true;
    return Date.now() - date <= THREE_WEEKS_MS;
};

export const bucketClassMemberActivities = (items: ClassMemberDashboardItem[]): BucketedClassMemberActivities => {
    const current: ClassMemberDashboardItem[] = [];
    const evaluations: ClassMemberDashboardItem[] = [];
    const older: ClassMemberDashboardItem[] = [];

    items.forEach((item) => {
        // Vocab visibilityDate is normalized to epoch ms in the extractor, so both kinds share
        // the test resolver here (null = HIDDEN for both).
        const { visibility } = resolveTestActivityVisibility(item.activity.visibilityDate);
        if (visibility !== ActivityVisibility.VISIBLE) return;

        if (item.kind === ClassMemberDashboardItemKinds.TEST && item.status === ClassMemberActivityStatuses.EVALUATED) {
            // Evaluations age out 3 weeks after the teacher released them (not after the activity
            // was shared) — a late-released result still surfaces in the evaluations section first.
            if (isWithinLastThreeWeeks(item.result?.sharedInClassroomDate)) {
                evaluations.push(item);
            } else {
                older.push(item);
            }
        } else if (isWithinLastThreeWeeks(item.activity.visibilityDate)) {
            current.push(item);
        } else {
            older.push(item);
        }
    });

    const byVisibilityDateDesc = (a: ClassMemberDashboardItem, b: ClassMemberDashboardItem) =>
        (b.activity.visibilityDate ?? 0) - (a.activity.visibilityDate ?? 0);
    const evaluationSortKey = (item: ClassMemberDashboardItem) =>
        (item.kind === ClassMemberDashboardItemKinds.TEST && item.result?.sharedInClassroomDate) || 0;

    current.sort(byVisibilityDateDesc);
    older.sort(byVisibilityDateDesc);
    evaluations.sort((a, b) => evaluationSortKey(b) - evaluationSortKey(a));

    return { current, evaluations, older };
};
